// src/components/RunningTally.tsx
// ─────────────────────────────────────────────────────────────────────────────
// Running Tally — Brief 12 Section 3
// Horizontal bar pinned below main nav, visible only in the Your Plan movement.
// Shows cumulative impact of all decisions made so far in the session.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';

export interface TallyProps {
  taxSavedAnnual: number;
  taxSavedCumulative: number;
  interestSaved: number;
  freedomAgeImprovement: number;   // years, can be decimal e.g. 2.5
  gapClosed: number;               // £ amount
  gapTotal: number;                // original gap — for % calculation
  highlightGap?: boolean;          // Brief 26 — make Gap closed prominent in Your Plan
  doNothingAge?: number;           // do-nothing retirement age from Voyant
  targetFreedomAge?: number;       // target freedom age from Pre-Session Setup
}

const fmt = (n: number) => {
  if (n === 0) return '£0';
  return '£' + Math.round(n).toLocaleString();
};

const RunningTally: React.FC<TallyProps> = ({
  taxSavedAnnual,
  taxSavedCumulative,
  interestSaved,
  freedomAgeImprovement,
  gapClosed,
  gapTotal,
  highlightGap,
  doNothingAge,
  targetFreedomAge,
}) => {
  const gapPct = gapTotal > 0 ? Math.round((gapClosed / gapTotal) * 100) : 0;

  // Projected freedom age — interpolates between do-nothing age and target freedom age
  // based on % of gap closed. Falls back gracefully if data is missing.
  let freedomLabel = '—';
  if (targetFreedomAge && targetFreedomAge > 0) {
    if (gapPct >= 100) {
      freedomLabel = `Age ${targetFreedomAge}`;
    } else if (doNothingAge && doNothingAge > 0) {
      const projectedAge = Math.round(
        doNothingAge - (gapPct / 100) * (doNothingAge - targetFreedomAge)
      );
      freedomLabel = `Age ${projectedAge}`;
    } else if (gapPct >= 50) {
      freedomLabel = `Age ${targetFreedomAge}`;
    }
  } else if (freedomAgeImprovement > 0) {
    freedomLabel = `${freedomAgeImprovement.toFixed(1)} yrs earlier`;
  }

  return (
    <div
      className="w-full flex-shrink-0"
      style={{ background: '#1B2B4B' }}
    >
      <div className="px-6 py-2.5 flex items-center justify-between gap-6 overflow-x-auto">
        {/* Label */}
        <div className="text-white/60 text-xs font-medium whitespace-nowrap flex-shrink-0">
          Today's impact:
        </div>

        {/* Figures */}
        <div className="flex items-center gap-6 text-xs whitespace-nowrap">
          <div className="flex items-center gap-1.5">
            <span className="text-white/70">Tax saved/yr</span>
            <span className="font-bold" style={{ color: '#C9A84C' }}>{fmt(taxSavedAnnual)}</span>
          </div>

          <div className="text-white/30">|</div>

          <div className="flex items-center gap-1.5">
            <span className="text-white/70">Tax saved total</span>
            <span className="font-bold" style={{ color: '#C9A84C' }}>{fmt(taxSavedCumulative)}</span>
          </div>

          <div className="text-white/30">|</div>

          <div className="flex items-center gap-1.5">
            <span className="text-white/70">Interest saved</span>
            <span className="font-bold" style={{ color: '#C9A84C' }}>{fmt(interestSaved)}</span>
          </div>

          <div className="text-white/30">|</div>

          <div
            className="flex items-center gap-1.5"
            style={highlightGap ? {
              background: 'rgba(100,218,210,0.15)',
              border: '1px solid #64DAD2',
              borderRadius: 6,
              padding: '2px 10px',
              marginLeft: -4,
              marginRight: -4,
            } : undefined}
          >
            <span className="text-white/70" style={highlightGap ? { fontSize: 13 } : undefined}>Gap closed</span>
            <span className="font-bold" style={{ color: '#64DAD2', fontSize: highlightGap ? 18 : undefined }}>
              {fmt(gapClosed)} ({gapPct}%)
            </span>
          </div>

          <div className="text-white/30">|</div>

          <div className="flex items-center gap-1.5">
            <span className="text-white/70">Freedom age</span>
            <span className="font-bold" style={{ color: '#C9A84C' }}>{freedomLabel}</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RunningTally;
